import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getBlogPostById, blogPosts } from '../data/portfolio';

const BlogPostPage = () => {
  const { postId } = useParams();
  const post = getBlogPostById(postId);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold font-['Syne'] mb-4">Post Not Found</h1>
          <Link to="/blog" className="btn-primary">Back to Blog</Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const relatedPosts = blogPosts
    .filter(p => p.id !== postId && p.category === post.category)
    .slice(0, 2);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="min-h-screen bg-white text-black">
      <SEO 
        title={post.title}
        description={post.excerpt}
        url={`/blog/${post.id}`}
        image={post.image}
        type="article"
      />
      <Header />
      
      <main className="pt-20">
        {/* Back Link */}
        <div className="container-custom pt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Blog</span>
          </Link>
        </div>

        {/* Article Header */}
        <article className="section-padding pb-0">
          <div className="container-custom max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="px-4 py-1.5 bg-gray-100 text-sm font-medium rounded-full">
                  {post.category}
                </span>
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] tracking-tight mb-8 leading-tight">
                {post.title}
              </h1>

              <p className="text-xl text-gray-600 mb-8">
                {post.excerpt}
              </p>

              <div className="flex items-center justify-between flex-wrap gap-6 pb-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <img
                    src={post.author.avatar}
                    alt={post.author.name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold">{post.author.name}</p>
                    <p className="text-gray-500 text-sm">{post.author.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">{formatDate(post.publishedAt)}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </article>

        {/* Featured Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="container-custom max-w-5xl py-12"
        >
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        {/* Article Content */}
        <section className="pb-16">
          <div className="container-custom max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="prose prose-lg prose-gray max-w-none 
                prose-headings:font-['Syne'] prose-headings:font-bold prose-headings:tracking-tight
                prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl
                prose-p:leading-relaxed prose-p:text-gray-700
                prose-a:text-black prose-a:underline prose-a:underline-offset-4
                prose-strong:text-black
                prose-ul:list-disc prose-ol:list-decimal
                prose-li:text-gray-700"
            >
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </motion.div>

            {/* Share */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex items-center justify-between flex-wrap gap-6 pt-12 mt-12 border-t border-gray-100"
            >
              <div className="flex items-center gap-4">
                <Share2 className="w-5 h-5 text-gray-400" />
                <span className="text-gray-500 text-sm">Share this article</span>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </motion.div>

            {/* Author Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8 mt-12"
            >
              <div className="flex items-start gap-6">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Written by</p>
                  <h3 className="text-xl font-bold font-['Syne'] mb-2">{post.author.name}</h3>
                  <p className="text-gray-500 mb-3">{post.author.role} at Bankki Media</p>
                  <p className="text-gray-600 text-sm">
                    Passionate about creative strategy and helping brands tell their stories in meaningful ways.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="section-padding bg-gray-50">
            <div className="container-custom">
              <h2 className="text-3xl font-bold font-['Syne'] mb-12">Related Articles</h2>
              <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                {relatedPosts.map((relatedPost, index) => (
                  <motion.article
                    key={relatedPost.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group"
                  >
                    <Link to={`/blog/${relatedPost.id}`}>
                      <div className="relative overflow-hidden mb-6 aspect-[16/10]">
                        <img
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                        />
                      </div>
                      <p className="text-gray-500 text-sm mb-2">{relatedPost.category}</p>
                      <h3 className="text-xl font-bold font-['Syne'] group-hover:text-gray-600 transition-colors">
                        {relatedPost.title}
                      </h3>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPostPage;
